import {
	ChangeEvent,
	ForwardedRef,
	KeyboardEvent,
	SyntheticEvent,
	forwardRef,
	useCallback,
} from "react";
import { KeyValue } from "@/common/constants/keyValue";
import { ContactLabel } from "@/components/atoms/contactLabel";
import { Flex, GapSize } from "@/components/atoms/flex";
import { Text, mergeClasses, Button } from "@fluentui/react-components";
import { useStyle } from "./style";
import { MultiInputItemProps, MultiInputProps } from "./types";
import { Dismiss16Regular } from "@fluentui/react-icons";

export const MultiInput = forwardRef(
	(props: MultiInputProps, ref: ForwardedRef<HTMLInputElement>) => {
		const {
			label,
			items,
			limit,
			classesName,
			inputValue,
			placeholder,
			autoFocus,
			isKeyDownHandled,
			showClearButton,
			onItemSelected,
			onInputChanged,
			onInputFocus,
			onInputBlur,
			onInputPaste,
			onInputClear,
			disabled,
		} = props;
		const classes = useStyle();

		const onInputKeyDownHandler = useCallback(
			(e: KeyboardEvent<HTMLInputElement>) => {
				if (e.key !== KeyValue.Enter) {
					return;
				}

				onContactEditingEvent(e);
			},
			[onItemSelected]
		);

		const onContactEditingEvent = useCallback(
			(e: SyntheticEvent<HTMLInputElement>) => {
				const value = e.currentTarget.value;

				if (!value) {
					return;
				}

				e.currentTarget.value = "";
				onItemSelected(value);
			},
			[onItemSelected]
		);

		const input = (
			<input
				disabled={disabled ?? false}
				ref={ref}
				autoFocus={autoFocus}
				value={inputValue}
				placeholder={placeholder}
				className={mergeClasses(
					classes.input,
					classesName?.input,
					showClearButton ? classes.inputClearButton : ""
				)}
				onChange={(e: ChangeEvent<HTMLInputElement>) =>
					!!onInputChanged && onInputChanged(e.target.value)
				}
				onKeyDown={isKeyDownHandled ? onInputKeyDownHandler : () => {}}
				onBlur={onInputBlur}
				onFocus={onInputFocus}
				onPaste={onInputPaste}
			/>
		);

		const getInput = () => {
			return showClearButton ? (
				<div className={classes.inputWrapper}>
					{input}
					<Button
						disabled={disabled ?? false}
						onClick={onInputClear}
						appearance="transparent"
						className={classes.clearButton}
						icon={<Dismiss16Regular />}
					/>
				</div>
			) : (
				input
			);
		};

		const isInputVisible = !limit || items.length < limit;

		return (
			<Flex
				vAlign="flex-start"
				hAlign="space-between"
				className={mergeClasses(classes.root, classesName?.root)}
			>
				{!!label && (
					<Text
						className={mergeClasses(classes.label, classesName?.label)}
						children={label}
						size={200}
					/>
				)}
				<Flex
					wrap="wrap"
					vAlign="center"
					hAlign="flex-start"
					gap={GapSize.Small}
					className={mergeClasses(classes.container, classesName?.container)}
				>
					{items.slice(0, limit).map((item: MultiInputItemProps) => (
						<ContactLabel
							key={item.key}
							text={item.value}
							onButtonClick={item.action}
							appearance={item.appearance}
							classesName={{ root: classesName?.contactLabel }}
							disabled={disabled ?? false}
						/>
					))}
					{isInputVisible && getInput()}
				</Flex>
			</Flex>
		);
	}
);
